import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { Box } from "@mui/material";
import { MyTimePageHeader } from "my-time/components";
import {
  allMyTimeRequests,
  allMyTimeRequestsIsMobile,
} from "my-time/configs/table-defs";
import { useMyTimeActualDate, useMyTimeUsersWithRequests } from "my-time/hooks";
import {
  OcCard,
  OcDataTable,
  OcPageNameTitle,
  OcPageTransition,
  OcRoleFilter,
} from "oc/components";
import { ProductsT, RoleCategoriesT } from "oc/configs/constants";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcButtonGroup,
  useOcTableState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcRoleFilters,
} from "oc/hooks";

export const MyTimeApproveRequestsPage = ({
  product = "MyTime",
  role = "examiner",
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const { date, formattedDate, handleDateChange } = useMyTimeActualDate();

  const buttonsDef = ["OC_TO_BE_ASSESSED", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MySheetMyTimesheet",
    buttonsDef,
  });

  const [onlyRequested, setOnlyRequested] = useState(true);
  const [prevOnlyRequested, setPrevOnlyRequested] = useState(true);

  const [showOvertime, setShowOvertime] = useState(true);
  const [prevShowOvertime, setPrevShowOvertime] = useState(true);

  const [showWorkingTimeChange, setShowWorkingTimeChange] = useState(true);
  const [prevShowWorkingTimeChange, setPrevShowWorkingTimeChange] =
    useState(true);

  const [showStandbyCall, setShowStandbyCall] = useState(true);
  const [prevShowStandbyCall, setPrevShowStandbyCall] = useState(true);

  const switchesDef = [
    {
      label: "MY_TIME_REPORTS_CHANGES",
      handleSwitchChange: () => {
        setShowWorkingTimeChange(!showWorkingTimeChange);
      },
      selected: showWorkingTimeChange,
    },
    {
      label: "MY_TIME_OVERTIMES",
      handleSwitchChange: () => {
        setShowOvertime(!showOvertime);
      },
      selected: showOvertime,
    },
    {
      label: "MY_TIME_CONSCRIPTIONS",
      handleSwitchChange: () => {
        setShowStandbyCall(!showStandbyCall);
      },
      selected: showStandbyCall,
    },
  ];

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  let tableId = "allMyTimeRequestsTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });

  const [, /*filterable*/ setFilterable] = useState(false);

  let { contractCode: contract } = useOcCurrentUserState();

  const handleCompleted = () => {
    handleChangeFilterable(false);
  };

  let startDate = moment(date).startOf("month").toDate().toISOString();
  let endDate = moment(date).endOf("month").toDate().toISOString();

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract: contract,
    product: product as ProductsT,
    role: role,
  });

  const { loadQuery, data, loading } = useMyTimeUsersWithRequests({
    showOvertime,
    showWorkingTimeChange,
    requestsOnly: onlyRequested,
    showStandbyCall,
    roleFilters,
    tableData,
    startDate: startDate,
    endDate: endDate,
    onCompleted: handleCompleted,
  });

  const handleClearFilters = () => {
    resetRoleFilters();
  };

  const handleChangeFilterable = (state: boolean) => {
    setFilterable(state);
  };

  useEffect(() => {
    if (selectedButton === "OC_TO_BE_ASSESSED") {
      setOnlyRequested(true);
    } else if (selectedButton === "OC_ALL") {
      setOnlyRequested(false);
    }
  }, [selectedButton, setOnlyRequested]);

  const buttonDisable =
    _.isEqual(roleFilters, prevRoleFilters) &&
    onlyRequested === prevOnlyRequested &&
    showOvertime === prevShowOvertime &&
    showWorkingTimeChange === prevShowWorkingTimeChange &&
    showStandbyCall === prevShowStandbyCall;

  const handleLoadQuery = () => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
    setPrevOnlyRequested(onlyRequested);
    setPrevShowOvertime(showOvertime); // <-- FIXED: use current state, not the "prev" state
    setPrevShowWorkingTimeChange(showWorkingTimeChange);
    setPrevShowStandbyCall(showStandbyCall);
  };

  const list = {
    rows: data?.rows,
    total: data?.total,
  };

  const originalonSelectRow = (o: any) => {
    if (isMobile) {
      history.push(
        `/my-time/employee-requests/${o?.row?.employee}/${o?.row?.contract}`,
      );
    } else {
      history.push(`/my-time/employee-requests/${o.employee}/${o.contract}`);
    }
  };

  const onSelectRow = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id);
    } else {
      originalonSelectRow(o);
    }
  };

  const [currentDate, setCurrentDate] = useState(date);
  const [prevCurrentDate, setPrevCurrentDate] = useState(date);

  const handleCurrentDateChange = (date: any) => {
    setPrevCurrentDate(currentDate);
    setCurrentDate(date);
    loadQuery();
  };

  if (currentDate !== prevCurrentDate) {
    loadQuery();
    setPrevCurrentDate(currentDate);
  }

  const handleTableDataChange = (state: typeof initialTableData) => {
    setTableData(state);
    loadQuery();
  };

  const tableDef = isMobile ? allMyTimeRequestsIsMobile : allMyTimeRequests;

  const filteredTableDef = {
    id: allMyTimeRequests?.id,
    columns: allMyTimeRequests?.columns?.filter(
      (column) =>
        !allMyTimeRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  useEffect(() => {
    loadQuery();
    setPrevRoleFilters(roleFilters);
  }, []); // eslint-disable-line

  return (
    <OcPageTransition>
      <OcPageNameTitle title={t("OC_APPROVE_REQUESTS")} goBack={false} />
      <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
        <MyTimePageHeader
          formattedDate={formattedDate}
          date={date}
          onDateChange={(date) => {
            handleDateChange(date);
            handleCurrentDateChange(date);
          }}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Box width="100%">
          <OcCard label={t("MY_TIME_REQUESTS")}>
            <OcRoleFilter
              subtypeProps={{
                roleFilters: roleFilters,
                queryType: "notAdmin",
                handleAddItem: handleAddFilter,
                handleRemoveItem: handleRemoveFilter,
                product: product as ProductsT,
                role: role as RoleCategoriesT,
                isCheckbox: true,
              }}
              OcButtonGroup={OcButtonGroup}
              switchesDef={switchesDef}
              summaryItems={[
                {
                  label: "OC_TOTAL_COUNT_OF_EMPLOYEES",
                  value: data?.allUsersCount || 0,
                },
                {
                  label: "OC_COUNT_OF_REQUESTS",
                  value: data?.allRequestCount || 0,
                  color: "primary",
                },
              ]}
              buttonBarProps={{
                handlePrimaryButton: () => {
                  handleLoadQuery();
                  handleChangeFilterable(true);
                },
                primaryButtonDisabled: buttonDisable,
                handleRightButton: handleClearFilters,
                noLine: true,
                paddingTop: "10px",
                rightButtonText: t("OC_RESET"),
                rightButtonId: "cancel-button",
                rightButtonColor: "secondary",
                primaryButtonText: t("OC_FILTERING"),
                primaryButtonId: "filter-button",
              }}
            />
            <Box paddingTop="32px">
              <OcDataTable
                tableDef={tableDef}
                tableId={tableId}
                tableData={tableData}
                setTableData={handleTableDataChange}
                data={list}
                loading={loading}
                onSelectRow={onSelectRow}
                pagination={true}
                filteredTableDef={filteredTableDef}
                handleMoreButton={originalonSelectRow}
                selectedRowId={selectedRowId}
              />
            </Box>
          </OcCard>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyTimeApproveRequestsPage;
